import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Grid,
  Typography,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';
import api from '../../services/api';
import { toast } from 'react-toastify';

export default function RegrasJuros() {
  const [regras, setRegras] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRegra, setEditingRegra] = useState(null);
  const [formData, setFormData] = useState({
    tipo: 'valor_fixo',
    base_calculo: 'valor_total',
    valor_minimo: '',
    valor_maximo: '',
    taxa: '',
    ativo: true,
    hora_execucao: new Date(2025, 0, 1, 7, 0), // 7:00 AM
    periodicidade: 'diario'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRegras();
  }, []);

  const loadRegras = async () => {
    try {
      const response = await api.get('/regras-juros');
      setRegras(response.data);
    } catch (error) {
      toast.error('Erro ao carregar regras de juros');
    }
  };

  const handleOpenDialog = (regra = null) => {
    if (regra) {
      setEditingRegra(regra);
      setFormData({
        ...regra,
        hora_execucao: parse(regra.hora_execucao, 'HH:mm:ss', new Date())
      });
    } else {
      setEditingRegra(null);
      setFormData({
        tipo: 'valor_fixo',
        base_calculo: 'valor_total',
        valor_minimo: '',
        valor_maximo: '',
        taxa: '',
        ativo: true,
        hora_execucao: new Date(2025, 0, 1, 7, 0),
        periodicidade: 'diario'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRegra(null);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        hora_execucao: format(formData.hora_execucao, 'HH:mm:ss')
      };

      if (editingRegra) {
        await api.put(`/regras-juros/${editingRegra.id}`, data);
        toast.success('Regra atualizada com sucesso');
      } else {
        await api.post('/regras-juros', data);
        toast.success('Regra criada com sucesso');
      }

      handleCloseDialog();
      loadRegras();
    } catch (error) {
      toast.error('Erro ao salvar regra de juros');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta regra?')) {
      try {
        await api.delete(`/regras-juros/${id}`);
        toast.success('Regra excluída com sucesso');
        loadRegras();
      } catch (error) {
        toast.error('Erro ao excluir regra');
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, pl: { xs: 1, md: 1.5 } }}>
      {/* Cabeçalho */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            Regras de Juros
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} sx={{ 
          display: 'flex', 
          justifyContent: { xs: 'flex-start', sm: 'flex-end' }, 
          mt: { xs: 1, sm: 0 },
          gap: 1
        }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={() => handleOpenDialog()}
            size="small"
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            Nova Regra
          </Button>
        </Grid>
      </Grid>

      {/* Tabela para desktop/tablet */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Paper sx={{ overflow: 'hidden', borderRadius: '10px' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Base de Cálculo</TableCell>
                  <TableCell>Valor Mínimo</TableCell>
                  <TableCell>Valor Máximo</TableCell>
                  <TableCell>Taxa</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {regras.map((regra) => (
                  <TableRow key={regra.id}>
                    <TableCell>
                      {regra.tipo === 'valor_fixo' ? 'Valor Fixo' : 'Percentual'}
                    </TableCell>
                    <TableCell>
                      {regra.base_calculo === 'valor_total' ? 'Valor Total' : 
                       regra.base_calculo === 'valor_parcela' ? 'Valor da Parcela' : 'Aporte'}
                    </TableCell>
                    <TableCell>R$ {regra.valor_minimo}</TableCell>
                    <TableCell>R$ {regra.valor_maximo}</TableCell>
                    <TableCell>
                      {regra.tipo === 'valor_fixo' 
                        ? `R$ ${regra.taxa}` 
                        : regra.tipo === 'percentual'
                        ? `${regra.taxa}%`
                        : `R$ ${regra.taxa} p/ cada R$ ${regra.valor_minimo}`}
                    </TableCell>
                    <TableCell>
                      {regra.ativo ? 
                        <span style={{ color: 'green' }}>Ativo</span> : 
                        <span style={{ color: 'gray' }}>Inativo</span>}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => handleOpenDialog(regra)} color="primary" size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(regra.id)} color="error" size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      
      {/* Cards para mobile */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {regras.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography>Nenhuma regra de juros cadastrada</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {regras.map((regra) => (
              <Paper
                key={regra.id}
                elevation={1}
                sx={{ p: 2, borderRadius: 1, border: '1px solid #e0e0e0' }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    {regra.tipo === 'valor_fixo' ? 'Valor Fixo' : 'Percentual'}
                  </Typography>
                  <Box>
                    {regra.ativo ? 
                      <Typography variant="body2" sx={{ color: 'green' }}>Ativo</Typography> : 
                      <Typography variant="body2" sx={{ color: 'gray' }}>Inativo</Typography>}
                  </Box>
                </Box>
                
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Base de Cálculo:
                    </Typography>
                    <Typography variant="body2">
                      {regra.base_calculo === 'valor_total' ? 'Valor Total' : 'Valor da Parcela'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                      Taxa:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                      {regra.tipo === 'valor_fixo' 
                        ? `R$ ${regra.taxa}` 
                        : `${regra.taxa}%`}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Valor Mínimo:
                    </Typography>
                    <Typography variant="body2">
                      R$ {regra.valor_minimo}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                      Valor Máximo:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'right' }}>
                      R$ {regra.valor_maximo}
                    </Typography>
                  </Grid>
                </Grid>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(regra)}
                  >
                    Editar
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(regra.id)}
                  >
                    Excluir
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRegra ? 'Editar Regra de Juros' : 'Nova Regra de Juros'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 10 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={formData.tipo}
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                  label="Tipo"
                >
                  <MenuItem value="valor_fixo">Valor Fixo</MenuItem>
                  <MenuItem value="percentual">Percentual</MenuItem>
                  <MenuItem value="proporcional">Proporcional (Aporte)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Base de Cálculo</InputLabel>
                <Select
                  value={formData.base_calculo || 'valor_total'}
                  onChange={(e) => handleInputChange('base_calculo', e.target.value)}
                  label="Base de Cálculo"
                >
                  <MenuItem value="valor_total">Valor Total do Empréstimo</MenuItem>
                  <MenuItem value="valor_parcela">Valor da Parcela</MenuItem>
                  <MenuItem value="aporte">Aporte (Total)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Valor Mínimo"
                type="number"
                value={formData.valor_minimo}
                onChange={(e) => handleInputChange('valor_minimo', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Valor Máximo"
                type="number"
                value={formData.valor_maximo}
                onChange={(e) => handleInputChange('valor_maximo', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={
                  formData.tipo === 'valor_fixo' ? 'Valor do Juros (R$)' : 
                  formData.tipo === 'percentual' ? 'Taxa de Juros (%)' : 
                  'Valor Juros Proporcional (R$)'
                }
                type="number"
                value={formData.taxa}
                onChange={(e) => handleInputChange('taxa', e.target.value)}
                helperText={formData.tipo === 'proporcional' ? 'Valor cobrado por cada base' : ''}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Periodicidade</InputLabel>
                <Select
                  value={formData.periodicidade || 'diario'}
                  onChange={(e) => handleInputChange('periodicidade', e.target.value)}
                  label="Periodicidade"
                >
                  <MenuItem value="diario">Diário</MenuItem>
                  <MenuItem value="semanal">Semanal</MenuItem>
                  <MenuItem value="mensal">Mensal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="Hora de Execução"
                  value={formData.hora_execucao}
                  onChange={(newValue) => handleInputChange('hora_execucao', newValue)}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      fullWidth: true,
                      margin: 'normal'
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.ativo}
                    onChange={(e) => handleInputChange('ativo', e.target.checked)}
                  />
                }
                label="Ativo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
